import React, { FC, useState } from 'react';
import {
  Button,
  CircleButton,
  ColorInput,
  FormTabSwitcher,
  Input,
  Select,
  TextArea,
  Toggle,
} from '@ui';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import useTranslation from '@app/hooks/use-translation';
import { OptionItem } from '@app/components/ui/select/select.type';
import { useNavigate } from 'react-router-dom';
import { MonitoringFormCreateType } from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';
import {
  useCreateMonitoringFormMutation,
  useEditMonitoringFormMutation,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { formFieldBaseStylesModal650, formFieldNameBaseStyles } from '@app/styles/base-styles';

import { toast } from 'react-toastify';

import { MonitoringFormSettingsOutletType } from '../../monitoring-forms-settings.outlet';

type FormType = MonitoringFormCreateType;

const eval_method_options = [
  { title: 'Взвешенная оценка', value: 'WEIGHT' },
  { title: 'Простая оценка', value: 'SCORE' },
];
const assessment_calc_options = [
  { title: 'Сумма', value: 'SUMM' },
  { title: 'Среднее', value: 'AVG' },
  { title: 'Доля', value: 'PROP' },
];

type MainSettingsFormPropsType = {
  newProject?: boolean;
};

const MainSettingsForm: FC<
  MainSettingsFormPropsType & Partial<MonitoringFormSettingsOutletType>
> = (props) => {
  const { newProject = false, analyticsProjects, monitoringFormData } = props;
  const { t } = useTranslation('pages.qmManage.monitoring_forms.createMonitoringForm');
  const { t: tF } = useTranslation('common.forms');

  const [finalScoreSwitchValue, setFinalScoreSwitchValue] = useState({
    min: typeof monitoringFormData?.evaluation?.score?.min === 'number',
    max: typeof monitoringFormData?.evaluation?.score?.max === 'number',
  });
  const [conditionalFormattingStatus, setConditionalFormattingStatus] = useState(
    !!monitoringFormData?.conditional_formatting.length || false,
  );
  // api
  const [createMF] = useCreateMonitoringFormMutation();
  const [editMF] = useEditMonitoringFormMutation();
  // api

  // eslint-disable-next-line
  const navigate = useNavigate();
  // Options
  const analyticsProjectsOptions: OptionItem[] =
    analyticsProjects?.map(({ project_id, name }) => ({
      value: project_id,
      title: name,
    })) || [];

  const appeal_options: OptionItem[] = [
    {
      title: t('day', { num: 3 }),
      value: 3,
    },
    {
      title: t('days', { num: 7 }),
      value: 7,
    },
    {
      title: t('days', { num: 14 }),
      value: 14,
    },
    {
      title: t('days', { num: 30 }),
      value: 30,
    },
    {
      title: t('days', { num: 60 }),

      value: 60,
    },
    {
      title: t('indefinitely'),
      value: 'null',
    },
  ];
  // eslint-disable-next-line
  const calc_deep_options: OptionItem[] = [
    {
      title: t('days', { num: 30 }),
      value: 30,
    },
    {
      title: t('days', { num: 60 }),
      value: 60,
    },
    {
      title: t('days', { num: 180 }),

      value: 180,
    },
  ];
  // Options

  const {
    register,
    setValue,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: monitoringFormData
      ? { ...monitoringFormData }
      : {
          evaluation: { calc: 'SUMM' },
          appellation: { deadline: 3 },
        },
  });
  const {
    fields,
    append: appendMainConditionalFormatting,
    remove: removeMainConditionalFormatting,
  } = useFieldArray({
    name: 'conditional_formatting',
    control,
  });

  const handleChangeFinalScoreToggle = (key: 'min' | 'max', value: boolean) => {
    setFinalScoreSwitchValue((prev) => ({ ...prev, [key]: value }));
    if (!value) {
      setValue(`evaluation.score.${key}`, undefined);
    } else {
      if (key === 'min') {
        setValue(`evaluation.score.${key}`, 0);
      } else {
        setValue(`evaluation.score.${key}`, 100);
      }
    }
  };

  function onSubmit(data: FormType) {
    if (newProject) {
      createMF({
        ...data,
        appellation: {
          ...data.appellation,
          deadline:
            typeof data.appellation.deadline === 'string' ? null : data.appellation.deadline,
        },
      })
        .unwrap()
        .then((data) => navigate(`../${data.id}/`));
      return;
    }
    monitoringFormData?.id &&
      editMF({
        body: {
          ...data,
          appellation: {
            ...data.appellation,
            deadline:
              typeof data.appellation.deadline === 'string' ? null : data.appellation.deadline,
          },
        },
        params: { id: monitoringFormData?.id },
      })
        .unwrap()
        .then(() => toast.success(t('successfully_changed')));
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full flex flex-col gap-[25px] relative max-w-[65%]"
    >
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('title')} />
        <Input
          {...register('name', {
            required: { value: true, message: tF('required') },
            maxLength: { value: 100, message: tF('max_len_error', { num: 100 }) },
          })}
          error={errors.name?.message}
        />
      </div>
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('description')} />
        <TextArea
          {...register('description', {
            maxLength: { value: 200, message: tF('max_len_error', { num: 200 }) },
          })}
          height={100}
          error={errors.description?.message}
        />
      </div>
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('ap_project')} />
        <Controller
          name="project.project_id"
          control={control}
          rules={{ required: tF('required') }}
          render={({ fieldState: { error } }) => (
            <Select
              withSearch
              options={analyticsProjectsOptions || []}
              defaultValue={watch('project.project_id')}
              error={error?.message}
              onChange={(projectId) => {
                const project = analyticsProjects?.find(
                  (project) => project.project_id === projectId,
                );
                if (!project) return;
                setValue('project', {
                  project_id: project?.project_id,
                  project_name: project?.name,
                  project_type: project?.project_type,
                });
              }}
            />
          )}
        />
      </div>
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('assessment_calc')} />
        <FormTabSwitcher
          valuesList={assessment_calc_options}
          isActive={watch('evaluation.calc')}
          onChange={(value: FormType['evaluation']['calc']) => setValue('evaluation.calc', value)}
        />
      </div>
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('final_score')} />
        <div className="flex flex-col gap-[20px]">
          <div className="flex items-center  gap-[15px] self-end">
            <LabelMiniJsx label={t('min')} />
            <div className="w-[100px]">
              <Input
                {...register('evaluation.score.min')}
                type={'number'}
                disabled={!finalScoreSwitchValue.min}
              />
            </div>
            <Toggle
              checked={finalScoreSwitchValue.min}
              onChecked={(value) => handleChangeFinalScoreToggle('min', value)}
            />
          </div>
          <div className="flex items-center  gap-[15px] self-end">
            <LabelMiniJsx label={t('max')} />
            <div className="w-[100px]">
              <Input
                {...register('evaluation.score.max')}
                type={'number'}
                disabled={!finalScoreSwitchValue.max}
              />
            </div>
            <Toggle
              checked={finalScoreSwitchValue.max}
              onChecked={(value) => handleChangeFinalScoreToggle('max', value)}
            />
          </div>
        </div>
      </div>
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('eval_method')} />
        <FormTabSwitcher
          disabled
          valuesList={eval_method_options}
          isActive={monitoringFormData?.rating_type || ''}
          onChange={() => console.log('')}
        />
      </div>
      <>
        <div className="flex items-center justify-between">
          <LabelMiniJsx label={t('conditional_formatting')} />
          <Toggle
            checked={conditionalFormattingStatus}
            onChecked={(val) => setConditionalFormattingStatus(val)}
          />
        </div>
        {conditionalFormattingStatus && (
          <div className="flex flex-col gap-[10px] self-end">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-[80px_80px_150px_auto_auto] items-center gap-x-[10px]"
              >
                <Input type="number" {...register(`conditional_formatting.${index}.value.0`)} />
                <Input type="number" {...register(`conditional_formatting.${index}.value.1`)} />
                <Input
                  {...register(`conditional_formatting.${index}.desc`)}
                  placeholder={t('description')}
                />
                <ColorInput
                  color={watch(`conditional_formatting.${index}.color`)}
                  changeColor={(color) => setValue(`conditional_formatting.${index}.color`, color)}
                />
                <CircleButton
                  icon={'TrashIcon'}
                  size={16}
                  className="text-basic_red"
                  onClick={() => removeMainConditionalFormatting(index)}
                />
              </div>
            ))}
            <div className="self-end flex">
              <CircleButton
                icon={'PlusIcon'}
                size={16}
                className="text-action"
                onClick={() =>
                  appendMainConditionalFormatting({ value: [0, 0], color: '', desc: '' })
                }
              />
            </div>
          </div>
        )}
      </>
      <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('appeal_deadline')} />
        <Select
          options={appeal_options}
          onChange={(value) => setValue('appellation.deadline', value)}
          defaultValue={
            watch('appellation.deadline') === null
              ? 'null'
              : watch('appellation.deadline') || undefined
          }
        />
      </div>
      {/* TODO:s */}
      {/* <div className={formFieldBaseStylesModal650}>
        <LabelMiniJsx label={t('calc_deep')} />
        <Select options={calc_deep_options} defaultValue={watch('recalc.deadline')} />
      </div> */}
      {/*BUTTON BLOCK*/}
      <div className="sticky bg-white bottom-0 py-[20px] flex items-center gap-[10px]">
        <Button
          disabled={monitoringFormData && monitoringFormData?.status !== 'NEW'}
          type="submit"
          label={t('approve_button')}
        />
        <Button fill="linked" link={-1} label={t('cancel_button')} />
      </div>
      {/*BUTTON BLOCK*/}
    </form>
  );
};

export default MainSettingsForm;

const LabelMiniJsx = (props: { label: string }) => {
  return <span className={formFieldNameBaseStyles}>{props.label}</span>;
};
